/** 5x7 bitmap font used to build the voxel text. Rows are strings of 0/1, top to bottom. */
const G: Record<string, string[]> = {
	A: ['01110', '10001', '10001', '11111', '10001', '10001', '10001'],
	B: ['11110', '10001', '10001', '11110', '10001', '10001', '11110'],
	C: ['01110', '10001', '10000', '10000', '10000', '10001', '01110'],
	D: ['11110', '10001', '10001', '10001', '10001', '10001', '11110'],
	E: ['11111', '10000', '10000', '11110', '10000', '10000', '11111'],
	F: ['11111', '10000', '10000', '11110', '10000', '10000', '10000'],
	G: ['01110', '10001', '10000', '10111', '10001', '10001', '01111'],
	H: ['10001', '10001', '10001', '11111', '10001', '10001', '10001'],
	I: ['01110', '00100', '00100', '00100', '00100', '00100', '01110'],
	J: ['00111', '00010', '00010', '00010', '00010', '10010', '01100'],
	K: ['10001', '10010', '10100', '11000', '10100', '10010', '10001'],
	L: ['10000', '10000', '10000', '10000', '10000', '10000', '11111'],
	M: ['10001', '11011', '10101', '10101', '10001', '10001', '10001'],
	N: ['10001', '10001', '11001', '10101', '10011', '10001', '10001'],
	O: ['01110', '10001', '10001', '10001', '10001', '10001', '01110'],
	P: ['11110', '10001', '10001', '11110', '10000', '10000', '10000'],
	Q: ['01110', '10001', '10001', '10001', '10101', '10010', '01101'],
	R: ['11110', '10001', '10001', '11110', '10100', '10010', '10001'],
	S: ['01111', '10000', '10000', '01110', '00001', '00001', '11110'],
	T: ['11111', '00100', '00100', '00100', '00100', '00100', '00100'],
	U: ['10001', '10001', '10001', '10001', '10001', '10001', '01110'],
	V: ['10001', '10001', '10001', '10001', '10001', '01010', '00100'],
	W: ['10001', '10001', '10001', '10101', '10101', '10101', '01010'],
	X: ['10001', '10001', '01010', '00100', '01010', '10001', '10001'],
	Y: ['10001', '10001', '10001', '01010', '00100', '00100', '00100'],
	Z: ['11111', '00001', '00010', '00100', '01000', '10000', '11111'],
	'0': ['01110', '10001', '10011', '10101', '11001', '10001', '01110'],
	'1': ['00100', '01100', '00100', '00100', '00100', '00100', '01110'],
	'2': ['01110', '10001', '00001', '00010', '00100', '01000', '11111'],
	'3': ['11111', '00010', '00100', '00010', '00001', '10001', '01110'],
	'4': ['00010', '00110', '01010', '10010', '11111', '00010', '00010'],
	'5': ['11111', '10000', '11110', '00001', '00001', '10001', '01110'],
	'6': ['00110', '01000', '10000', '11110', '10001', '10001', '01110'],
	'7': ['11111', '00001', '00010', '00100', '01000', '01000', '01000'],
	'8': ['01110', '10001', '10001', '01110', '10001', '10001', '01110'],
	'9': ['01110', '10001', '10001', '01111', '00001', '00010', '01100'],
	'.': ['00', '00', '00', '00', '00', '11', '11'],
	':': ['00', '11', '11', '00', '11', '11', '00'],
	'-': ['00000', '00000', '00000', '11111', '00000', '00000', '00000'],
	'/': ['00001', '00010', '00010', '00100', '01000', '01000', '10000'],
	'!': ['1', '1', '1', '1', '1', '0', '1'],
	'?': ['01110', '10001', '00001', '00010', '00100', '00000', '00100'],
	'%': ['11001', '11010', '00010', '00100', '01000', '01011', '10011'],
	' ': ['000', '000', '000', '000', '000', '000', '000'],
}

export const GLYPH_H = 7
const LINE_GAP = 2

export interface Layout {
	/** [col, row] of every lit cell, rows counted from the top of the block. */
	cells: [number, number][]
	width: number
	height: number
	lines: number
}

/** Lay out text (may contain \n). Each line is centred within the widest line. */
export function layoutText(text: string): Layout {
	const lines = text.toUpperCase().split('\n')
	const rows = lines.map((line) => {
		const cells: [number, number][] = []
		let x = 0
		for (const ch of line) {
			const g = G[ch] ?? G['?']
			const w = g[0].length
			for (let r = 0; r < GLYPH_H; r++) for (let c = 0; c < w; c++) if (g[r][c] === '1') cells.push([x + c, r])
			x += w + 1
		}
		return { cells, width: Math.max(0, x - 1) }
	})
	const width = Math.max(...rows.map((r) => r.width), 1)
	const cells: [number, number][] = []
	rows.forEach((row, i) => {
		const dx = Math.floor((width - row.width) / 2)
		const dy = i * (GLYPH_H + LINE_GAP)
		for (const [c, r] of row.cells) cells.push([c + dx, r + dy])
	})
	return { cells, width, height: lines.length * GLYPH_H + (lines.length - 1) * LINE_GAP, lines: lines.length }
}

/** Greedy wrap on separators so an IPv6 address becomes 2 or 4 lines instead of a 40-character strip. */
export function wrapText(text: string, maxChars: number): string {
	if (text.includes('\n') || text.length <= maxChars) return text
	const tokens = text.split(/(?<=[.:\- ])/)
	const lines: string[] = []
	let cur = ''
	for (const tok of tokens) {
		if (cur && (cur + tok).trim().length > maxChars) {
			lines.push(cur.trim())
			cur = tok
		} else cur += tok
	}
	if (cur.trim()) lines.push(cur.trim())
	return lines.join('\n')
}
